import React, { useCallback, useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import FocusCard from "@/components/journey/FocusCard";
import MomentCard from "@/components/journey/MomentCard";
import RoomReadinessCard from "@/components/journey/RoomReadinessCard";

/**
 * JourneyGuide — the card-based Guide Journey page.
 *
 * This is the Phase 1 replacement for the illustrated hallway experience.
 * All cards read from existing SONMI data:
 *   · Moment (NOW)         ← /api/journey/guide  (Moment engine)
 *   · Room Readiness       ← /api/journey/guide  (tile grid)
 *   · Focus                ← /api/journey/guide  (pattern summary)
 *
 * NOW is a stable structural card whose contents evolve. It does not
 * disappear when empty — it renders an intentional quiet state.
 *
 * Rebook & Membership is documented as a future card that requires a
 * live Zenoti signal (`service_ended`) and an MT-recommendation model
 * before it can honestly speak. It is intentionally not rendered here.
 */

const DEFERRED_KIND_LABEL = {
  guest_arrival_preparation: "Guest arrival preparation",
  guest_handoff: "Guest handoff",
  checkout_rebook_followthrough: "Checkout · rebook · membership",
};

export default function JourneyGuide() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/journey/guide");
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load Guide Journey.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCheckToggle = useCallback(
    async (key, value) => {
      setBusyKey(key);
      try {
        await api.post("/me/today/check", { key, value });
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't update — try again.");
      } finally {
        setBusyKey(null);
      }
    },
    [load]
  );

  return (
    <div className="min-h-screen bg-stone-50">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="journey-guide-page">
        <PageHeader user={user} data={data} loading={loading} />

        {error && (
          <div
            className="rounded-2xl subtle-border bg-white p-6 text-sm text-stone-600"
            data-testid="journey-guide-error"
          >
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            <MomentCard
              moments={data.moments}
              onCheckToggle={handleCheckToggle}
              busyKey={busyKey}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <RoomReadinessCard tiles={data.room_readiness} />
              </div>
              <div>
                <FocusCard focus={data.focus} />
              </div>
            </div>

            <DeferredNote deferred={data.deferred_moment_kinds} />
          </>
        )}
      </main>
    </div>
  );
}

function PageHeader({ user, data, loading }) {
  const boutique = data?.boutique_code || user?.boutique_code || "";
  return (
    <header
      className="bg-white subtle-border rounded-2xl p-8 card-shadow"
      data-testid="journey-guide-header"
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] label-eyebrow text-stone-400">Journey</div>
          <div
            className="font-serif-display text-3xl text-stone-900 mt-1 tracking-wide"
            style={{ fontWeight: 400 }}
          >
            Guide Journey
          </div>
          <p className="text-sm text-stone-500 mt-2 max-w-xl leading-relaxed">
            The rhythm of a Guide's day. NOW carries what needs your hands.
            Focus watches the patterns.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] label-eyebrow text-stone-400">Today</div>
          <div className="font-serif-display text-xl text-stone-900" style={{ fontWeight: 400 }}>
            {loading ? "…" : boutique || "—"}
          </div>
          <div className="text-[10px] label-eyebrow text-stone-400 mt-1">
            {loading ? "" : (data?.date || "")}
          </div>
        </div>
      </div>
    </header>
  );
}

function DeferredNote({ deferred }) {
  const list = deferred || [];
  if (list.length === 0) return null;
  return (
    <section
      className="bg-white subtle-border rounded-2xl p-6 text-xs text-stone-500 leading-relaxed"
      data-testid="deferred-note"
    >
      <div className="text-[10px] label-eyebrow text-stone-400 mb-2">
        Not yet honest to display
      </div>
      <p className="mb-3">
        These Moment types will surface here when their underlying signals
        exist. NOW stays quiet on them today rather than showing something
        the system cannot verify.
      </p>
      <ul className="space-y-1.5">
        {list.map((d) => (
          <li key={d.kind} className="flex items-baseline gap-2">
            <span className="w-1 h-1 rounded-full bg-stone-300 shrink-0 translate-y-1.5" aria-hidden="true" />
            <span>
              <span className="text-stone-800">{DEFERRED_KIND_LABEL[d.kind] || d.kind}</span>
              <span className="text-stone-400"> · {d.reason} · awaits {d.future_dependency}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
